/**
 * Joist Sync API Service
 */

import { apiClient } from './client'
import type {
  JoistImportResponse,
  JoistLeadFilterParams,
  JoistLeadListResponse,
  JoistLeadResponse,
  JoistPromoteAllResponse,
  JoistPromoteResponse,
  JoistScanRequest,
  JoistScanResponse,
} from '@/types/api'

/**
 * Upload an estimates + clients CSV pair and get back the joined preview.
 *
 * Sent as multipart via `postForm`, which omits Content-Type so the browser can set
 * the boundary itself.
 */
export async function scanJoistExport(data: JoistScanRequest): Promise<JoistScanResponse> {
  const form = new FormData()
  form.append('estimates_file', data.estimatesFile)
  form.append('clients_file', data.clientsFile)
  form.append('time_window', data.timeWindow)
  if (data.startDate) form.append('start_date', data.startDate)
  if (data.endDate) form.append('end_date', data.endDate)

  return apiClient.postForm<JoistScanResponse>('/joist-leads/scan', form)
}

/** Re-read a scan. Lets a reload recover the preview without re-uploading. */
export async function getJoistScan(scanId: string): Promise<JoistScanResponse> {
  return apiClient.get<JoistScanResponse>(`/joist-leads/scans/${scanId}`)
}

/** Insert a scan's usable rows into leads_joist. */
export async function importJoistScan(scanId: string): Promise<JoistImportResponse> {
  return apiClient.post<JoistImportResponse>(`/joist-leads/scans/${scanId}/import`)
}

export async function getJoistLeads(
  params?: JoistLeadFilterParams
): Promise<JoistLeadListResponse> {
  return apiClient.get<JoistLeadListResponse>('/joist-leads', { params })
}

export async function getJoistLead(id: string): Promise<JoistLeadResponse> {
  return apiClient.get<JoistLeadResponse>(`/joist-leads/${id}`)
}

/** Promote one imported Joist lead into the leads table. */
export async function promoteJoistLead(id: string): Promise<JoistPromoteResponse> {
  return apiClient.post<JoistPromoteResponse>(`/joist-leads/${id}/promote`)
}

/**
 * Promote every pending Joist lead, normally scoped to the batch just imported.
 * Looped server-side so a large batch is one request rather than one per lead.
 */
export async function promoteAllJoistLeads(
  scanId?: string
): Promise<JoistPromoteAllResponse> {
  return apiClient.post<JoistPromoteAllResponse>('/joist-leads/promote-all', {
    scan_id: scanId ?? null,
  })
}

export const joistService = {
  scanJoistExport,
  getJoistScan,
  importJoistScan,
  getJoistLeads,
  getJoistLead,
  promoteJoistLead,
  promoteAllJoistLeads,
}
